/**
 * A router in eighty lines, because Stage 1 had six screens and two of them
 * took an id.
 *
 * Stage 0's version returned a string and said it should be thrown away when
 * item pages arrived. They have, so a route is now a small object -- the id is
 * parsed once, here, rather than by whichever screen happened to need it -- and
 * that is still the whole of the change. There is no library: the app has six
 * screens, no nested layouts, no loaders and no code splitting, and a router
 * that does all of those would be the largest dependency in the project by a
 * wide margin.
 *
 * What it does have to get right is the back button, because capture is a loop
 * somebody walks in and out of dozens of times in a sitting.
 */

import { useCallback, useEffect, useState } from "react";
import type { LabelKind } from "@iltocat/shared";

export type Route =
  | { name: "home" }
  | { name: "signin" }
  | { name: "admin" }
  | { name: "sharing" }
  | { name: "export" }
  | { name: "reports" }
  | { name: "settings" }
  | { name: "scan" }
  | { name: "import" }
  /**
   * Labels for items (one, or a selection: `/labels/item/a,b,c`), or for one
   * location and everything below it. Ids never contain a comma (`mintId`).
   */
  | { name: "labels"; kind: LabelKind; ids: string[] }
  /** Stage 7's four screens. They exist only in a stock inventory; the shell hides them elsewhere. */
  | { name: "stock" }
  | { name: "receive" }
  /** The client list, or one client and what is out there. */
  | { name: "clients"; id: string | null }
  | { name: "billing" }
  /** Back stock: reserves and what they hold. */
  | { name: "reserves" }
  /** The capture screen. `/new`, because that is what it is. */
  | { name: "capture" }
  | { name: "item"; id: string }
  /** The location tree, or one node of it. */
  | { name: "locations"; id: string | null }
  /** Filing the unfiled pile one item at a time. */
  | { name: "sort" }
  /**
   * Search, with its state in the query string -- `query` is everything after
   * the `?` -- so that the back button from an item lands on the same results
   * and a search can be bookmarked or saved as a link.
   */
  | { name: "search"; query: string }
  /**
   * Enrichment. The chosen items are in the address (`?ids=a,b`), read by the
   * screen itself as search reads its query; `ids` here is only for navigating
   * there with a choice already made.
   */
  | { name: "enrich"; ids: string[] }
  /** The listing for one item: `/item/:id/listing`. */
  | { name: "listing"; id: string };

const HOME: Route = { name: "home" };

export function routeOf(pathname: string): Route {
  const [, first = "", second = "", third = ""] = pathname.split("/");

  if (first === "signin") return { name: "signin" };
  if (first === "admin") return { name: "admin" };
  if (first === "sharing") return { name: "sharing" };
  if (first === "export") return { name: "export" };
  if (first === "reports") return { name: "reports" };
  // `/account` was this screen's address before it was called Settings.
  if (first === "settings" || first === "account") return { name: "settings" };
  if (first === "scan") return { name: "scan" };
  if (first === "import") return { name: "import" };
  if (first === "labels" && (second === "item" || second === "location") && third) {
    const ids = decodeURIComponent(third).split(",").filter(Boolean);
    return { name: "labels", kind: second, ids: second === "location" ? ids.slice(0, 1) : ids };
  }
  if (first === "stock") return { name: "stock" };
  if (first === "receive") return { name: "receive" };
  if (first === "clients") return { name: "clients", id: second ? decodeURIComponent(second) : null };
  if (first === "billing") return { name: "billing" };
  if (first === "reserves") return { name: "reserves" };
  if (first === "new") return { name: "capture" };
  if (first === "item" && second && third === "listing") {
    return { name: "listing", id: decodeURIComponent(second) };
  }
  if (first === "item" && second) return { name: "item", id: decodeURIComponent(second) };
  // The query itself is the search screen's to read: it is the one screen whose
  // state lives in the address, and it reads and rewrites it as it goes.
  if (first === "search") return { name: "search", query: "" };
  if (first === "enrich") return { name: "enrich", ids: [] };
  if (first === "sort") return { name: "sort" };
  if (first === "locations") return { name: "locations", id: second ? decodeURIComponent(second) : null };

  // Anything else is home rather than a 404 screen: the worker's single-page
  // fallback means a typo in the address bar lands here, and the useful answer
  // to that is the app.
  return HOME;
}

export function pathOf(route: Route): string {
  switch (route.name) {
    case "home":
      return "/";
    case "signin":
      return "/signin";
    case "admin":
      return "/admin";
    case "sharing":
      return "/sharing";
    case "export":
      return "/export";
    case "reports":
      return "/reports";
    case "settings":
      return "/settings";
    case "scan":
      return "/scan";
    case "import":
      return "/import";
    case "labels":
      return `/labels/${route.kind}/${route.ids.map(encodeURIComponent).join(",")}`;
    case "stock":
      return "/stock";
    case "receive":
      return "/receive";
    case "clients":
      return route.id ? `/clients/${encodeURIComponent(route.id)}` : "/clients";
    case "billing":
      return "/billing";
    case "reserves":
      return "/reserves";
    case "capture":
      return "/new";
    case "item":
      return `/item/${encodeURIComponent(route.id)}`;
    case "listing":
      return `/item/${encodeURIComponent(route.id)}/listing`;
    case "locations":
      return route.id ? `/locations/${encodeURIComponent(route.id)}` : "/locations";
    case "sort":
      return "/sort";
    case "search":
      return route.query ? `/search?${route.query}` : "/search";
    case "enrich":
      return route.ids.length ? `/enrich?ids=${route.ids.map(encodeURIComponent).join(",")}` : "/enrich";
  }
}

export function useRouter(): {
  route: Route;
  go: (route: Route) => void;
  back: () => void;
  /**
   * Counts calls to `go`. A screen keyed on it starts fresh on every visit even
   * when the path has not changed, which is what pressing "Items" while already
   * looking at filtered items should do.
   */
  visit: number;
} {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const [visit, setVisit] = useState(0);

  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const go = useCallback((route: Route) => {
    const path = pathOf(route);
    // The query string is dropped on purpose: the only ones this app receives
    // are `?signin=ok` and `?error=...`, which have already been read by the
    // time anything navigates, and leaving them behind makes a reload replay a
    // message about something that happened minutes ago. Where it came from
    // rides along, so a screen can tell "back" from "somewhere new" (enrich
    // returning to the item it was opened from).
    window.history.pushState({ from: window.location.pathname }, "", path);
    // Only the path is the route; the search screen carries its state after the ?.
    setPathname(path.split("?")[0] ?? path);
    setVisit((count) => count + 1);
    // A new screen starts at the top. Without this, saving an item from the
    // bottom of a long grid lands you at the bottom of the next screen.
    window.scrollTo(0, 0);
  }, []);

  /**
   * Real history, not a guess at where somebody came from. Cancelling a capture
   * should return to whatever was underneath it -- the grid, a shelf, an item
   * -- and only fall back to home when there is nothing underneath.
   */
  const back = useCallback(() => {
    if (window.history.length > 1) window.history.back();
    else go(HOME);
  }, [go]);

  return { route: routeOf(pathname), go, back, visit };
}

/** The path the current history entry was reached from by `go`, if it was. */
export function cameFrom(): string | null {
  const state: unknown = window.history.state;
  return state && typeof state === "object" && "from" in state && typeof state.from === "string"
    ? state.from
    : null;
}

/** The `?error=` codes the worker redirects back with, as sentences. */
export const SIGN_IN_ERRORS: Record<string, string> = {
  "bad-link": "That link has already been used or has expired. Ask for a new one.",
  "too-many": "Too many tries from here. Wait ten minutes and try again.",
  "not-invited": "That address is not on the invite list.",
  disabled: "That account has been disabled.",
  failed: "Something went wrong creating that account. Try again.",
  "oauth-failed": "That sign-in did not go through. Try again.",
  "oauth-unavailable": "That sign-in method is not set up here yet.",
  "oauth-unverified":
    "Microsoft could not confirm that address is yours, so it cannot sign you in by itself yet. Sign in another way, then connect Microsoft in Settings.",
  "oauth-in-use": "That account already signs in to a different utucat account.",
};

const PROVIDER_NAMES: Record<string, string> = { google: "Google", microsoft: "Microsoft" };

/**
 * What Settings says after connecting Google or Microsoft: the worker
 * redirects to `/settings?linked=<provider>` or `/settings?link-error=<code>`,
 * the second using the same codes as sign-in.
 */
export interface LinkOutcome {
  ok: boolean;
  message: string;
}

export function linkOutcomeFrom(query: URLSearchParams): LinkOutcome | null {
  const linked = query.get("linked");
  if (linked) {
    return {
      ok: true,
      message: `${PROVIDER_NAMES[linked] ?? "That account"} is connected. It can sign you in now.`,
    };
  }
  const error = query.get("link-error");
  if (error) return { ok: false, message: SIGN_IN_ERRORS[error] ?? SIGN_IN_ERRORS["oauth-failed"]! };
  return null;
}
